import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminSessionGuard } from '../hooks/useAdminSessionGuard';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Users, Award, TrendingUp, DollarSign, Loader2, X, Download,
  CheckCircle, AlertTriangle, RefreshCw, FileText, Code2, ListChecks, ScrollText, Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DECISIONS = [
  { key: 'verified', label: 'Verified', cls: 'bg-emerald-600 hover:bg-emerald-500', Icon: CheckCircle },
  { key: 'provisional', label: 'Provisional', cls: 'bg-amber-600 hover:bg-amber-500', Icon: ShieldCheck },
  { key: 'reassessment', label: 'Re-assessment', cls: 'bg-indigo-600 hover:bg-indigo-500', Icon: RefreshCw },
  { key: 'not_verified', label: 'Not Verified', cls: 'bg-red-600 hover:bg-red-500', Icon: AlertTriangle },
];

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500"><Icon size={13} className={tone} /> {label}</div>
    <p className="mt-2 text-3xl font-bold text-white tabular-nums" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{value}</p>
  </div>
);

const VerifiedAdminPage = () => {
  const navigate = useNavigate();
  useAdminSessionGuard((msg) => { toast.error(msg); navigate('/admin'); });
  const token = localStorage.getItem('admin_token');
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [audit, setAudit] = useState([]);
  const [plansData, setPlansData] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});
  const [newCoupon, setNewCoupon] = useState({ code: '', type: 'percentage', value: '', max_discount: '', min_amount: '', usage_limit: '', per_user_limit: '', expires_at: '' });
  const [timingCfg, setTimingCfg] = useState({ total_interview_minutes: 45, question_seconds: 120, coding_seconds: 600, mcq_seconds: 300 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, p, au, pl, cp] = await Promise.all([
        axios.get(`${API}/api/admin/verified-talent/analytics`, H),
        axios.get(`${API}/api/admin/verified-talent/profiles?status=all&limit=200`, H),
        axios.get(`${API}/api/admin/verified-talent/audit?limit=100`, H),
        axios.get(`${API}/api/admin/verified-talent/plans`, H),
        axios.get(`${API}/api/admin/verified-talent/coupons`, H),
      ]);
      setAnalytics(a.data);
      setProfiles(p.data.profiles || []);
      setAudit(au.data.logs || []);
      setPlansData(pl.data.plans || []);
      setCoupons(cp.data.coupons || []);
      try {
        const cfg = await axios.get(`${API}/api/verified-talent/config`);
        if (cfg.data?.timings) setTimingCfg(cfg.data.timings);
      } catch { /* ignore */ }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) toast.error('Admin session expired — please log in again');
      else toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => { if (token) loadAll(); else setLoading(false); }, [token, loadAll]);

  const openCandidate = async (studentId) => {
    setDetailLoading(true); setDetail({ loading: true }); setReason('');
    try {
      const res = await axios.get(`${API}/api/admin/verified-talent/candidate/${studentId}`, H);
      setDetail(res.data);
    } catch { toast.error('Could not load candidate'); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const applyDecision = async (decision) => {
    if (!reason.trim()) { toast.info('A reason is required for overrides'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/verified-talent/candidate/${detail.student.id}/decision`, { decision, reason }, H);
      toast.success(`Marked as ${decision}`);
      setDetail(null); setReason('');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply decision'); }
    finally { setSaving(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/verified-talent/export`, { ...H, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'verified_talent_profiles.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const savePlanPrice = async (plan) => {
    const price = priceEdits[plan.id];
    if (price === undefined || price === '') return;
    try {
      await axios.put(`${API}/api/admin/verified-talent/plans/${plan.id}`, { price: Number(price) }, H);
      toast.success(`${plan.name} price updated`);
      setPriceEdits((e) => { const n = { ...e }; delete n[plan.id]; return n; });
      loadAll();
    } catch { toast.error('Failed to update price'); }
  };

  const createCoupon = async () => {
    if (!newCoupon.code.trim() || !newCoupon.value) { toast.info('Code and value are required'); return; }
    try {
      await axios.post(`${API}/api/admin/verified-talent/coupons`, { ...newCoupon, applies_to: ['all'] }, H);
      toast.success('Coupon created');
      setNewCoupon({ code: '', type: 'percentage', value: '', max_discount: '', min_amount: '', usage_limit: '', per_user_limit: '', expires_at: '' });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create coupon'); }
  };

  const toggleCoupon = async (c) => {
    try { await axios.put(`${API}/api/admin/verified-talent/coupons/${c.id}`, { active: !c.active }, H); loadAll(); }
    catch { toast.error('Failed to update coupon'); }
  };

  const deleteCoupon = async (c) => {
    try { await axios.delete(`${API}/api/admin/verified-talent/coupons/${c.id}`, H); toast.success('Coupon deleted'); loadAll(); }
    catch { toast.error('Failed to delete coupon'); }
  };

  const saveTimings = async () => {
    try {
      await axios.put(`${API}/api/admin/verified-talent/config`, {
        total_interview_minutes: Number(timingCfg.total_interview_minutes) || 45,
        question_seconds: Number(timingCfg.question_seconds) || 120,
        coding_seconds: Number(timingCfg.coding_seconds) || 600,
        mcq_seconds: Number(timingCfg.mcq_seconds) || 300,
      }, H);
      toast.success('Interview timings saved');
    } catch { toast.error('Failed to save timings'); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#07070B] flex flex-col items-center justify-center text-center px-6">
        <ShieldCheck size={40} className="text-purple-400 mb-3" />
        <p className="text-zinc-300">Admin authentication required.</p>
        <Link to="/admin" className="mt-4 px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm font-semibold">Go to Admin Login</Link>
      </div>
    );
  }

  const filtered = profiles.filter((p) => {
    if (!q) return true;
    const s = `${p.student_name || ''} ${p.student_email || ''} ${p.student_college || ''}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-[#07070B] text-white" data-testid="verified-admin-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-white mb-2" data-testid="back-to-admin-link">← Back to Admin Panel</Link>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <ShieldCheck className="text-purple-400" /> Verified Profile Admin
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Manage verification candidates, decisions and audit trail.</p>
          </div>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm font-semibold" data-testid="export-csv-btn">
            <Download size={15} /> Export CSV
          </button>
        </div>

        <div className="flex gap-2 mb-6 border-b border-white/10">
          {['overview', 'candidates', 'pricing', 'audit'].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-semibold capitalize border-b-2 -mb-px ${tab === t ? 'border-purple-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`} data-testid={`tab-${t}`}>{t}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-400 gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : (
          <>
            {tab === 'overview' && analytics && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-overview">
                <StatCard icon={Users} label="Total Profiles" value={analytics.total_profiles} tone="text-sky-300" />
                <StatCard icon={Award} label="Active Badges" value={analytics.active_badges} tone="text-emerald-300" />
                <StatCard icon={FileText} label="Total Attempts" value={analytics.total_attempts} tone="text-purple-300" />
                <StatCard icon={TrendingUp} label="Pass Rate" value={`${analytics.pass_rate}%`} tone="text-amber-300" />
                <StatCard icon={CheckCircle} label="Passed" value={analytics.passed} tone="text-emerald-300" />
                <StatCard icon={AlertTriangle} label="Failed" value={analytics.failed} tone="text-red-300" />
                <StatCard icon={DollarSign} label="Revenue" value={`₹${analytics.revenue}`} tone="text-emerald-300" />
                <StatCard icon={Users} label="Paying Students" value={analytics.paying_students} tone="text-sky-300" />
              </div>
            )}

            {tab === 'candidates' && (
              <div>
                <div className="relative mb-4 max-w-sm">
                  <Search size={15} className="absolute left-3 top-2.5 text-zinc-500" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, college…" className="w-full bg-white/[0.03] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm" data-testid="candidate-search" />
                </div>
                <div className="overflow-x-auto rounded-2xl border border-white/10">
                  <table className="w-full text-sm" data-testid="candidates-table">
                    <thead className="bg-white/[0.03] text-zinc-400 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">Candidate</th>
                        <th className="text-left px-4 py-3">College</th>
                        <th className="text-left px-4 py-3">Score</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-right px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p) => (
                        <tr key={p.student_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                          <td className="px-4 py-3">
                            <p className="font-semibold text-white">{p.student_name || '—'}</p>
                            <p className="text-xs text-zinc-500">{p.student_email}</p>
                          </td>
                          <td className="px-4 py-3 text-zinc-400">{p.student_college || '—'}</td>
                          <td className="px-4 py-3 tabular-nums">{Math.round(p.best_score || 0)}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${p.is_verified ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/10 text-zinc-400'}`}>{p.status || 'none'}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => openCandidate(p.student_id)} className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-xs font-semibold" data-testid={`view-candidate-${p.student_id}`}>Review</button>
                          </td>
                        </tr>
                      ))}
                      {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">No candidates found.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'pricing' && (
              <div className="space-y-8" data-testid="pricing-tab">
                {/* Interview timings */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-3">Interview timings</h3>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="timings-form">
                    {[
                      ['total_interview_minutes', 'Total interview (min)'],
                      ['question_seconds', 'Per question (sec)'],
                      ['coding_seconds', 'Coding challenge (sec)'],
                      ['mcq_seconds', 'MCQ round (sec)'],
                    ].map(([k, label]) => (
                      <div key={k}>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
                        <input
                          type="number"
                          value={timingCfg[k]}
                          onChange={(e) => setTimingCfg((s) => ({ ...s, [k]: e.target.value }))}
                          className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
                          data-testid={`timing-${k}`}
                        />
                      </div>
                    ))}
                    <button onClick={saveTimings} className="col-span-2 md:col-span-4 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm font-bold" data-testid="save-timings-btn">Save timings</button>
                  </div>
                </div>

                {/* Plan prices */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-3">Plan pricing</h3>
                  <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
                    {plansData.map((p) => (
                      <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`price-row-${p.code}`}>
                        <div>
                          <p className="font-semibold text-white">{p.name} <span className="text-xs text-zinc-500">· {p.code}</span></p>
                          <p className="text-xs text-zinc-500">{p.attempts} attempts · {p.validity_months} mo {p.is_active ? '' : '· inactive'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-500 text-sm">₹</span>
                          <input
                            type="number"
                            defaultValue={p.price}
                            onChange={(e) => setPriceEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                            className="w-24 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-1.5 text-sm"
                            data-testid={`price-input-${p.code}`}
                          />
                          <button onClick={() => savePlanPrice(p)} className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-xs font-bold" data-testid={`price-save-${p.code}`}>Save</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Coupons */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-3">Discount coupons</h3>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="coupon-form">
                    <input value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} placeholder="CODE" className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm uppercase" data-testid="new-coupon-code" />
                    <select value={newCoupon.type} onChange={(e) => setNewCoupon({ ...newCoupon, type: e.target.value })} className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="new-coupon-type">
                      <option value="percentage">Percentage %</option>
                      <option value="fixed">Fixed ₹</option>
                    </select>
                    <input value={newCoupon.value} onChange={(e) => setNewCoupon({ ...newCoupon, value: e.target.value })} placeholder="Value" type="number" className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="new-coupon-value" />
                    <input value={newCoupon.max_discount} onChange={(e) => setNewCoupon({ ...newCoupon, max_discount: e.target.value })} placeholder="Max ₹ (opt)" type="number" className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                    <input value={newCoupon.min_amount} onChange={(e) => setNewCoupon({ ...newCoupon, min_amount: e.target.value })} placeholder="Min purchase ₹" type="number" className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                    <input value={newCoupon.usage_limit} onChange={(e) => setNewCoupon({ ...newCoupon, usage_limit: e.target.value })} placeholder="Total uses (opt)" type="number" className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                    <input value={newCoupon.per_user_limit} onChange={(e) => setNewCoupon({ ...newCoupon, per_user_limit: e.target.value })} placeholder="Per user (opt)" type="number" className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                    <input value={newCoupon.expires_at} onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })} placeholder="Expiry ISO (opt)" className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                    <button onClick={createCoupon} className="col-span-2 md:col-span-4 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-bold" data-testid="create-coupon-btn">Create coupon</button>
                  </div>
                  <div className="rounded-2xl border border-white/10 divide-y divide-white/5" data-testid="coupons-list">
                    {coupons.length === 0 && <div className="px-4 py-6 text-center text-zinc-500 text-sm">No coupons yet.</div>}
                    {coupons.map((c) => (
                      <div key={c.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`coupon-${c.code}`}>
                        <div>
                          <p className="font-mono font-bold text-white">{c.code} <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${c.active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/10 text-zinc-500'}`}>{c.active ? 'active' : 'inactive'}</span></p>
                          <p className="text-xs text-zinc-500">{c.type === 'percentage' ? `${c.value}% off` : `₹${c.value} off`}{c.max_discount ? ` (max ₹${c.max_discount})` : ''} · used {c.used_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ''}{c.expires_at ? ` · exp ${String(c.expires_at).slice(0, 10)}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => toggleCoupon(c)} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-xs font-semibold" data-testid={`toggle-coupon-${c.code}`}>{c.active ? 'Disable' : 'Enable'}</button>
                          <button onClick={() => deleteCoupon(c)} className="px-3 py-1.5 rounded-lg bg-red-600/80 hover:bg-red-600 text-xs font-semibold" data-testid={`delete-coupon-${c.code}`}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tab === 'audit' && (
              <div className="rounded-2xl border border-white/10 divide-y divide-white/5" data-testid="audit-log">
                {audit.length === 0 && <div className="px-4 py-8 text-center text-zinc-500 text-sm">No admin actions recorded yet.</div>}
                {audit.map((l) => (
                  <div key={l.id} className="px-4 py-3 flex items-start gap-3 text-sm">
                    <ScrollText size={15} className="text-zinc-500 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-200"><strong className="text-white">{l.admin}</strong> · <span className="text-purple-300">{l.action}</span> · <span className="text-zinc-500 font-mono text-xs">{l.target_student_id}</span></p>
                      {l.details?.reason && <p className="text-xs text-zinc-500 mt-0.5">Reason: {l.details.reason}</p>}
                    </div>
                    <span className="text-[11px] text-zinc-600 flex-shrink-0">{l.created_at ? new Date(l.created_at).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Candidate detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl h-full bg-[#0A0A0F] border-l border-white/10 overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="candidate-drawer">
            <div className="sticky top-0 bg-[#0A0A0F] border-b border-white/10 px-5 py-3 flex items-center justify-between z-10">
              <span className="font-semibold">Candidate Review</span>
              <button onClick={() => setDetail(null)} className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </div>
            {detailLoading || detail.loading ? (
              <div className="flex items-center justify-center py-20 text-zinc-400 gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>
            ) : (
              <div className="p-5 space-y-5">
                <div>
                  <h3 className="text-xl font-bold">{detail.student?.full_name}</h3>
                  <p className="text-sm text-zinc-500">{detail.student?.email} · {detail.student?.college}</p>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    <span className="px-2 py-1 rounded bg-white/5 border border-white/10">Score: {Math.round(detail.profile?.best_score || 0)}</span>
                    <span className="px-2 py-1 rounded bg-white/5 border border-white/10">Status: {detail.profile?.status}</span>
                    {detail.verification_id && <span className="px-2 py-1 rounded bg-white/5 border border-white/10 font-mono">{detail.verification_id}</span>}
                    {detail.profile?.manual_decision && <span className="px-2 py-1 rounded bg-purple-500/15 text-purple-300">Override: {detail.profile.manual_decision}</span>}
                  </div>
                </div>

                {/* Coding submission */}
                {detail.latest_coding && (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-2"><Code2 size={13} /> Coding: {detail.latest_coding.title}</p>
                    {detail.latest_coding.submission ? (
                      <>
                        <p className="text-xs text-zinc-400">Tests: {detail.latest_coding.submission.tests_passed}/{detail.latest_coding.submission.tests_total} · {detail.latest_coding.submission.evaluation?.summary}</p>
                        <pre className="mt-2 text-[11px] bg-black/40 rounded p-2 overflow-auto max-h-40 text-zinc-300">{detail.latest_coding.submission.code}</pre>
                      </>
                    ) : <p className="text-xs text-zinc-500">Not submitted.</p>}
                  </div>
                )}

                {/* MCQ */}
                {detail.latest_mcq && (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1"><ListChecks size={13} /> MCQ Round</p>
                    <p className="text-xs text-zinc-400">{detail.latest_mcq.status === 'submitted' ? `Score ${detail.latest_mcq.score}% (${detail.latest_mcq.correct}/${detail.latest_mcq.total})` : 'Not submitted'}</p>
                  </div>
                )}

                {/* Transcript */}
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-2"><FileText size={13} /> Interview transcript</p>
                  <div className="space-y-2 max-h-72 overflow-auto" data-testid="transcript">
                    {(detail.latest_transcript || []).map((m, i) => (
                      <div key={i} className={`text-xs leading-relaxed ${m.role === 'assistant' ? 'text-purple-200' : 'text-zinc-300'}`}>
                        <span className="font-bold">{m.role === 'assistant' ? 'AI' : 'Candidate'}: </span>{m.content}
                      </div>
                    ))}
                    {(detail.latest_transcript || []).length === 0 && <p className="text-xs text-zinc-500">No transcript.</p>}
                  </div>
                </div>

                {/* Decision override */}
                <div className="rounded-xl border border-purple-500/20 bg-purple-500/[0.04] p-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-purple-200 mb-2">Manual override (reason required)</p>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why are you overriding? (audited)" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm mb-3" data-testid="override-reason" />
                  <div className="flex flex-wrap gap-2">
                    {DECISIONS.map((d) => (
                      <button key={d.key} onClick={() => applyDecision(d.key)} disabled={saving} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 ${d.cls}`} data-testid={`decision-${d.key}`}>
                        <d.Icon size={13} /> {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifiedAdminPage;
